<script setup lang="ts">
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import { getActivityIconFromSlug } from '@/composables/activityFeedIcons'
import ActivityCardBase from './ActivityCardBase.vue'
import {
  isImportedPointType,
  isWebappPointType,
  legacyWebappPointSubtypes,
  webappPointSubtypes
} from './pointActivityTypes'
import { getLocalizedActivityText } from './localizedActivityText'

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const isManual = computed(() => {
  return props.activity.type === 'points.manual'
})

const isImported = computed(() => {
  return isImportedPointType(props.activity.type)
})

const isExpiration = computed(() => {
  return props.activity.type === 'customer_point' && props.activity.data?.type === 'expiration_command'
})

const isWebapp = computed(() => {
  return isWebappPointType(props.activity.type) || (props.activity.type === 'customer_point' && !isExpiration.value)
})

const storeName = computed(() => {
  return (
    props.activity.data?.store_name ||
    props.activity.data?.store ||
    t('pages.customer.customerDetailPage.activities.noStore')
  )
})

const description = computed(() => {
  const descText = getLocalizedActivityText(props.activity.description, locale.value)
  if (descText) return descText

  const points = props.activity.data?.points || 0

  if (isManual.value) {
    return t('pages.customer.customerDetailPage.blocks.activity.manualPoints.text', { points })
  }
  if (isImported.value) {
    return t('pages.customer.customerDetailPage.blocks.activity.importedPoints.text', { points })
  }
  if (isExpiration.value) {
    return t('pages.customer.customerDetailPage.blocks.activity.expiration_command.text', {
      points: Math.abs(points)
    })
  }
  if (isWebapp.value) {
    const webappType = isWebappPointType(props.activity.type)
      ? webappPointSubtypes[props.activity.type]
      : props.activity.data?.type
    if (!webappType || !legacyWebappPointSubtypes.includes(webappType)) {
      return t('pages.customer.customerDetailPage.blocks.activity.unknown.text')
    }
    return t(`pages.customer.customerDetailPage.blocks.activity.${webappType}.text`, {
      points,
      store: storeName.value
    })
  }

  return ''
})

const title = computed(() => {
  return getLocalizedActivityText(props.activity.title, locale.value) || ''
})

const icon = computed((): Icons => {
  const fromSlug = getActivityIconFromSlug(props.activity.icon)
  if (fromSlug) return fromSlug
  if (isExpiration.value) return 'HeroClockIconSolid'
  return 'HeroCursorArrowRaysIconSolid'
})

const iconColor = computed(() => {
  if (isManual.value) return 'bg-pink-600'
  if (isImported.value) return 'bg-emerald-600'
  if (isExpiration.value) return 'bg-red-600'
  return 'bg-amber-500'
})
</script>

<template>
  <ActivityCardBase
    :icon="icon"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template v-if="title" #title>
      <p class="text-sm font-medium text-foreground">{{ title }}</p>
    </template>
    <template #description>
      <p v-if="description" class="text-sm text-muted-foreground" :class="{ 'mt-1': title }">{{ description }}</p>
    </template>
  </ActivityCardBase>
</template>
